import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';

import { customViewLineDiff } from './customViewDiff';

const PART_LABELS = { html: 'HTML', css: 'CSS', javascript: 'JavaScript' } as const;
const MARKS = { same: '  ', removed: '- ', added: '+ ' } as const;

/** One part of a custom view in an askRichard proposal: only the lines that change, not the whole code twice. */
export default function CustomViewCodeDiff({
  part,
  before,
  after,
}: {
  part: keyof typeof PART_LABELS;
  before: string;
  after: string;
}) {
  const diff = useMemo(() => customViewLineDiff(before, after), [before, after]);
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2">
        Custom view · {PART_LABELS[part]}: {diff.removed} {diff.removed === 1 ? 'line' : 'lines'} removed, {diff.added}{' '}
        added
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          fontFamily: 'monospace',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        {diff.lines.map((line, index) =>
          line.kind === 'gap' ? (
            <Box key={index} component="span" sx={{ display: 'block', color: 'text.disabled' }}>
              {line.text}
            </Box>
          ) : (
            <Box
              key={index}
              component="span"
              sx={{
                display: 'block',
                color:
                  line.kind === 'removed' ? 'error.main' : line.kind === 'added' ? 'success.main' : 'text.secondary',
              }}
            >
              <Box component="span" sx={{ color: 'text.disabled', userSelect: 'none' }}>
                {String(line.line).padStart(5)}{' '}
              </Box>
              {MARKS[line.kind]}
              {line.text}
            </Box>
          ),
        )}
        {diff.truncated && (
          <Box component="span" sx={{ display: 'block', color: 'text.disabled' }}>
            … more changes not shown
          </Box>
        )}
      </Box>
    </Box>
  );
}
